import React, { forwardRef } from 'react'
import { TextField as MuiTextField, TextFieldProps, InputAdornment, useTheme, useMediaQuery } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { NumberField as ShellNumberField } from '@e3dc-react/shell/Elements/InputFields/NumberField'
import { useSuffixFieldStyles } from '../../hooks/useSuffixFieldStyles'

const useStyles = makeStyles()(theme => ({
  suffix: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: 34,
    marginLeft: theme.spacing(2),
    paddingInline: theme.spacing(2),
    borderLeft: `1px solid ${theme.palette.shades.shade02}`,
    '& > span': {
      minWidth: 48,
      textAlign: 'center',
    },
  },
  emptyAdornment: {
    paddingRight: theme.spacing(2),
  },
}))

type BaseNumberFieldProps = {
  suffix?: string
} & TextFieldProps

/**
 * Styled MUI TextField (type="number") used directly for non-form scenarios, or wrap with react-hook-form Controller.
 */
export const BaseNumberField = forwardRef<typeof ShellNumberField, BaseNumberFieldProps>((props, ref) => {
  const { suffix, fullWidth } = props
  const { classes } = useStyles()
  const { classes: suffixFieldClasses, cx } = useSuffixFieldStyles()
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const applyFullWidth = fullWidth || downSm

  return (
    <MuiTextField
      {...props}
      inputRef={ref}
      type="number"
      fullWidth={applyFullWidth}
      className={suffix ? cx(props.className, suffixFieldClasses.suffixField) : props.className}
      InputProps={{
        endAdornment: suffix ? (
          <div className={classes.suffix}>
            <span>{suffix}</span>
          </div>
        ) : (
          <InputAdornment position="end" className={classes.emptyAdornment} />
        ),
      }}
    />
  )
})

BaseNumberField.displayName = 'BaseNumberField'
